import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import Tooltip from '../../../../../../../components/Tooltip';
import {
  AddIcon,
  CheckCircleIcon,
  CopyIcon,
  EditIcon,
  NotAllowedIcon,
  RepeatIcon,
  SearchIcon,
  ViewIcon,
  ViewOffIcon,
} from '@chakra-ui/icons';
import { useEffect, useMemo, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import ReactSelect from 'react-select';
import makeAnimated from 'react-select/animated';

import { collection, doc, getDocs, query, where } from 'firebase/firestore';
import firebaseFirestore from '~/config/firebase/firestore';
import { handleIsInvalidField } from '~/utils/general';
import { Location } from '~/core/types';
import { Company } from '../../../locations/presenter/page/Locations';
import {
  AdminUserRow,
  createUser,
  generatePassword,
  getReadableError,
  listCompanyUsers,
  revokeUserSessions,
  setUserDisabled,
  updateUser,
} from '../../data/adminUsersService';
import { FiLogOut } from 'react-icons/fi';

interface RoleData {
  id: string;
  name: string;
}

const ROLE_COLORS: Record<string, string> = {
  superadmin: 'purple',
  admin: 'blue',
  'location-manager': 'teal',
};

const UsersSettings = () => {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [company, setCompany] = useState<Company>();
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [userSelected, setUserSelected] = useState<AdminUserRow>();
  const [locations, setLocations] = useState<Location[]>([]);
  const [roles, setRoles] = useState<RoleData[]>([]);
  const [search, setSearch] = useState('');
  const [togglingUid, setTogglingUid] = useState<string>();
  const [revokingUid, setRevokingUid] = useState<string>();
  const [userToDisable, setUserToDisable] = useState<AdminUserRow>();
  const cancelDisableRef = useRef<HTMLButtonElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const getCompanies = async () => {
    const snapshot = await getDocs(collection(firebaseFirestore, 'companies'));
    setCompanies(
      snapshot.docs.map((docSnap) => ({ name: docSnap.data().name, id: docSnap.id }))
    );
  };

  const getRoles = async () => {
    const snapshot = await getDocs(collection(firebaseFirestore, 'roles'));
    setRoles(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })) as RoleData[]);
  };

  const getLocationsByCompany = async (companyId: string) => {
    const companyRef = doc(firebaseFirestore, 'companies', companyId);
    const q = query(collection(firebaseFirestore, 'locations'), where('company', '==', companyRef));
    const snapshot = await getDocs(q);
    setLocations(snapshot.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id })) as Location[]);
  };

  const loadUsers = async (companyId: string) => {
    setIsLoading(true);
    try {
      setUsers(await listCompanyUsers(companyId));
    } catch (error) {
      toast({ title: getReadableError(error), status: 'error', isClosable: true });
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Desactivar pide confirmación; reactivar no la necesita porque no quita accesos.
  const requestToggle = (user: AdminUserRow) => {
    if (user.disabled) {
      applyToggle(user);
      return;
    }
    setUserToDisable(user);
  };

  const applyToggle = async (user: AdminUserRow) => {
    const nextDisabled = !user.disabled;
    const action = nextDisabled ? 'desactivar' : 'reactivar';

    setUserToDisable(undefined);
    setTogglingUid(user.uid);
    try {
      await setUserDisabled(user.uid, nextDisabled);
      setUsers((current) =>
        current.map((row) => (row.uid === user.uid ? { ...row, disabled: nextDisabled } : row))
      );
      toast({
        title: nextDisabled ? 'Cuenta desactivada' : 'Cuenta reactivada',
        description: user.email,
        status: 'success',
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: `No se pudo ${action} la cuenta`,
        description: getReadableError(error),
        status: 'error',
        isClosable: true,
      });
    } finally {
      setTogglingUid(undefined);
    }
  };

  // Cierra la sesión del usuario en todos sus dispositivos. No cambia su
  // contraseña ni desactiva la cuenta: puede volver a entrar cuando quiera.
  const onRevokeSessions = async (user: AdminUserRow) => {
    setRevokingUid(user.uid);
    try {
      await revokeUserSessions(user.uid);
      toast({
        title: 'Sesión cerrada',
        description: `${user.email} tendrá que volver a iniciar sesión.`,
        status: 'success',
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'No se pudo cerrar la sesión',
        description: getReadableError(error),
        status: 'error',
        isClosable: true,
      });
    } finally {
      setRevokingUid(undefined);
    }
  };

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users;
    return users.filter(
      (user) =>
        user.name.toLowerCase().includes(term) || user.email.toLowerCase().includes(term)
    );
  }, [users, search]);

  useEffect(() => {
    getCompanies();
    getRoles();
  }, []);

  useEffect(() => {
    if (!company) return;
    setSearch('');
    getLocationsByCompany(company.id);
    loadUsers(company.id);
  }, [company]);

  return (
    // minW=0 evita que la tabla, al ser más ancha que la pantalla, estire
    // esta columna y empuje la cabecera fuera del área visible.
    <Flex flexDirection={'column'} gap={6} pb={10} minW={0} maxW={'100%'}>
      <FormControl maxW={'320px'}>
        <FormLabel>Negocio</FormLabel>
        <Select
          placeholder='Seleccione un negocio'
          value={company?.id ?? ''}
          onChange={(e) => setCompany(companies.find((item) => item.id === e.target.value))}
        >
          {companies.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </Select>
      </FormControl>

      {!company && (
        <Box borderWidth={'1px'} borderRadius={'md'} borderStyle={'dashed'} p={10}>
          <Text textAlign={'center'} color={'gray.500'}>
            Seleccione un negocio para ver y gestionar sus usuarios.
          </Text>
        </Box>
      )}

      {company && (
        <Flex flexDirection={'column'} gap={4}>
          <Flex justifyContent={'space-between'} alignItems={'center'} gap={4} flexWrap={'wrap'}>
            <HStack>
              <Text fontWeight={'bold'}>Usuarios</Text>
              <Badge colorScheme='gray'>{users.length}</Badge>
            </HStack>

            <HStack>
              <InputGroup size='sm' maxW={'260px'}>
                <Input
                  borderRadius={'md'}
                  placeholder='Buscar por nombre o correo'
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <InputRightElement pointerEvents='none'>
                  <SearchIcon color='gray.400' />
                </InputRightElement>
              </InputGroup>

              <Button
                leftIcon={<AddIcon />}
                colorScheme='blue'
                size='sm'
                // Sin esto el botón se encoge junto al buscador y el texto
                // se corta.
                flexShrink={0}
                whiteSpace={'nowrap'}
                onClick={() => {
                  setUserSelected(undefined);
                  onOpen();
                }}
              >
                Nuevo usuario
              </Button>
            </HStack>
          </Flex>

          {isLoading && <Progress size='xs' isIndeterminate />}

          {!isLoading && filteredUsers.length === 0 && (
            <Box borderWidth={'1px'} borderRadius={'md'} borderStyle={'dashed'} p={10}>
              <Text textAlign={'center'} color={'gray.500'}>
                {users.length === 0
                  ? 'Este negocio todavía no tiene usuarios. Cree el primero con “Nuevo usuario”.'
                  : 'Ningún usuario coincide con la búsqueda.'}
              </Text>
            </Box>
          )}

          {filteredUsers.length > 0 && (
            <TableContainer borderWidth={'1px'} borderRadius={'md'}>
              <Table variant='simple' size='sm'>
                <Thead bg={'gray.50'}>
                  <Tr>
                    <Th>Nombre</Th>
                    <Th>Correo</Th>
                    <Th>Rol</Th>
                    <Th>Ubicaciones</Th>
                    <Th>Estado</Th>
                    <Th textAlign={'right'}>Acciones</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredUsers.map((user) => (
                    <Tr key={user.userCompanyId} opacity={user.disabled ? 0.55 : 1}>
                      <Td fontWeight={'medium'}>{user.name || '—'}</Td>
                      <Td>{user.email}</Td>
                      <Td>
                        <Badge colorScheme={ROLE_COLORS[user.role?.name] ?? 'gray'}>
                          {user.role?.name || '—'}
                        </Badge>
                      </Td>
                      <Td>
                        {/* Resumen en lugar de la lista completa: con 15 salas
                            cada fila ocupaba media pantalla. */}
                        {(user.locations?.length ?? 0) === 0 ? (
                          <Text color={'gray.500'}>Todas</Text>
                        ) : (
                          <Tooltip
                            label={user.locations?.map((l) => l.name).join(', ')}
                            hasArrow
                          >
                            <Text cursor={'help'} whiteSpace={'nowrap'}>
                              {user.locations!.length === 1
                                ? user.locations![0].name
                                : `${user.locations!.length} ubicaciones`}
                            </Text>
                          </Tooltip>
                        )}
                      </Td>
                      <Td>
                        <Badge colorScheme={user.disabled ? 'red' : 'green'}>
                          {user.disabled ? 'Desactivada' : 'Activa'}
                        </Badge>
                      </Td>
                      <Td>
                        <HStack justifyContent={'flex-end'} spacing={1}>
                          <Tooltip label='Editar usuario'>
                            <IconButton
                              aria-label='Editar usuario'
                              icon={<EditIcon />}
                              size='sm'
                              variant='ghost'
                              onClick={() => {
                                setUserSelected(user);
                                onOpen();
                              }}
                            />
                          </Tooltip>
                          <Tooltip label='Cerrar su sesión en todos los dispositivos'>
                            <IconButton
                              aria-label='Cerrar sesión del usuario'
                              icon={<FiLogOut />}
                              size='sm'
                              variant='ghost'
                              colorScheme='orange'
                              isDisabled={user.disabled}
                              isLoading={revokingUid === user.uid}
                              onClick={() => onRevokeSessions(user)}
                            />
                          </Tooltip>
                          <Tooltip label={user.disabled ? 'Reactivar cuenta' : 'Desactivar cuenta'}>
                            <IconButton
                              aria-label={user.disabled ? 'Reactivar cuenta' : 'Desactivar cuenta'}
                              icon={user.disabled ? <CheckCircleIcon /> : <NotAllowedIcon />}
                              size='sm'
                              variant='ghost'
                              colorScheme={user.disabled ? 'green' : 'red'}
                              isLoading={togglingUid === user.uid}
                              onClick={() => requestToggle(user)}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </Flex>
      )}

      {company && isOpen && (
        <UserModal
          key={userSelected?.userCompanyId ?? 'new-user'}
          user={userSelected}
          isOpen={isOpen}
          company={company}
          locations={locations}
          roles={roles}
          onClose={() => {
            setUserSelected(undefined);
            onClose();
          }}
          onSaved={() => {
            setUserSelected(undefined);
            onClose();
            loadUsers(company.id);
          }}
        />
      )}

      <AlertDialog
        isOpen={Boolean(userToDisable)}
        leastDestructiveRef={cancelDisableRef}
        onClose={() => setUserToDisable(undefined)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize='lg' fontWeight='bold'>
              Desactivar cuenta
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text>
                <strong>{userToDisable?.name || userToDisable?.email}</strong> no podrá
                volver a iniciar sesión.
              </Text>
              <Text mt={3} color={'gray.600'} fontSize={'sm'}>
                No se borra nada: sus reservas y su historial se conservan, y puede
                reactivar la cuenta en cualquier momento.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter gap={3}>
              <Button ref={cancelDisableRef} variant='ghost' onClick={() => setUserToDisable(undefined)}>
                Cancelar
              </Button>
              <Button
                colorScheme='red'
                onClick={() => userToDisable && applyToggle(userToDisable)}
              >
                Desactivar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Flex>
  );
};

const animatedComponents = makeAnimated();

type UserModalProps = {
  user?: AdminUserRow;
  isOpen: boolean;
  company: Company;
  locations: Location[];
  roles: RoleData[];
  onClose: () => void;
  onSaved: () => void;
};

const UserModal = ({
  user,
  isOpen,
  company,
  locations,
  roles,
  onClose,
  onSaved,
}: UserModalProps) => {
  const toast = useToast();
  const isEditing = Boolean(user);
  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>(
    user?.locations?.map((location) => location.id) ?? []
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<IFormInputs>({
    mode: 'onBlur',
    resolver: yupResolver(schema(isEditing)),
    defaultValues: {
      user_name: user?.name ?? '',
      email: user?.email ?? '',
      rol: user?.role?.id ?? '',
      password: '',
      confirmPassword: '',
    },
  });

  const watchPassword = watch('password');

  const locationsOptions = useMemo(
    () => locations.map((location) => ({ value: location.id, label: location.name })),
    [locations]
  );

  const selectedOptions = useMemo(
    () => locationsOptions.filter((option) => selectedLocationIds.includes(option.value)),
    [locationsOptions, selectedLocationIds]
  );

  const onGeneratePassword = async () => {
    const generated = generatePassword();
    setValue('password', generated, { shouldValidate: true });
    setValue('confirmPassword', generated, { shouldValidate: true });
    setShowPassword(true);
    try {
      await navigator.clipboard.writeText(generated);
      toast({
        title: 'Contraseña generada y copiada',
        description: 'Guárdela antes de cerrar: no podrá volver a verla.',
        status: 'info',
        duration: 8000,
        isClosable: true,
      });
    } catch {
      toast({
        title: 'Contraseña generada',
        description: 'Cópiela manualmente antes de guardar.',
        status: 'info',
        isClosable: true,
      });
    }
  };

  const onCopyPassword = async () => {
    if (!watchPassword) return;
    try {
      await navigator.clipboard.writeText(watchPassword);
      toast({ title: 'Contraseña copiada', status: 'success', duration: 2000 });
    } catch {
      toast({ title: 'No se pudo copiar', status: 'error', duration: 2000 });
    }
  };

  const onSubmit = async (data: IFormInputs) => {
    setIsSaving(true);
    try {
      if (isEditing && user) {
        await updateUser({
          uid: user.uid,
          userCompanyId: user.userCompanyId,
          name: data.user_name,
          email: data.email,
          ...(data.password ? { password: data.password } : {}),
          roleId: data.rol,
          locationIds: selectedLocationIds,
        });
        toast({
          title: 'Usuario actualizado',
          description: data.password ? 'Se cambió también la contraseña.' : undefined,
          status: 'success',
          isClosable: true,
        });
      } else {
        await createUser({
          name: data.user_name,
          email: data.email,
          password: data.password,
          roleId: data.rol,
          companyId: company.id,
          locationIds: selectedLocationIds,
        });
        toast({
          title: 'Usuario creado',
          description: `${data.email} ya puede iniciar sesión.`,
          status: 'success',
          isClosable: true,
        });
      }
      onSaved();
    } catch (error) {
      toast({
        title: isEditing ? 'No se pudo actualizar' : 'No se pudo crear el usuario',
        description: getReadableError(error),
        status: 'error',
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={!isSaving} size='lg'>
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>
            <VStack align={'flex-start'} spacing={0}>
              <Text>{isEditing ? 'Editar usuario' : 'Crear usuario'}</Text>
              <Text fontSize={'sm'} fontWeight={'normal'} color={'gray.500'}>
                {company.name}
              </Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton isDisabled={isSaving} />

          <ModalBody>
            <FormControl mb={4} isInvalid={handleIsInvalidField(errors.user_name?.message)}>
              <FormLabel>Nombre</FormLabel>
              <Input type='text' {...register('user_name')} />
              <FormErrorMessage>{errors.user_name?.message}</FormErrorMessage>
            </FormControl>

            <FormControl mb={4} isInvalid={handleIsInvalidField(errors.email?.message)}>
              <FormLabel>Correo</FormLabel>
              <Input type='email' {...register('email')} />
              <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
            </FormControl>

            <FormControl mb={4}>
              <FormLabel>Ubicaciones</FormLabel>
              <ReactSelect
                closeMenuOnSelect={false}
                components={animatedComponents}
                isMulti
                placeholder='Todas las ubicaciones'
                noOptionsMessage={() => 'Este negocio no tiene ubicaciones'}
                className='basic-multi-select'
                classNamePrefix='select'
                value={selectedOptions}
                options={locationsOptions}
                onChange={(selected) =>
                  setSelectedLocationIds((selected ?? []).map((option) => option.value))
                }
              />
              <FormHelperText>
                Si no selecciona ninguna, el usuario verá todas las ubicaciones.
              </FormHelperText>
            </FormControl>

            <FormControl mb={4} isInvalid={handleIsInvalidField(errors.rol?.message)}>
              <FormLabel>Rol</FormLabel>
              <Select placeholder='Seleccione un rol' {...register('rol')}>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.rol?.message}</FormErrorMessage>
            </FormControl>

            <Box borderTopWidth={'1px'} pt={4}>
              <HStack justifyContent={'space-between'} mb={2}>
                <Text fontWeight={'medium'}>
                  {isEditing ? 'Cambiar contraseña' : 'Contraseña'}
                </Text>
                <Button
                  size='xs'
                  variant='outline'
                  leftIcon={<RepeatIcon />}
                  onClick={onGeneratePassword}
                >
                  Generar
                </Button>
              </HStack>

              <FormControl mb={3} isInvalid={handleIsInvalidField(errors.password?.message)}>
                <InputGroup>
                  <Input
                    autoComplete='new-password'
                    type={showPassword ? 'text' : 'password'}
                    placeholder={isEditing ? 'Dejar vacío para no cambiarla' : ''}
                    {...register('password')}
                  />
                  <InputRightElement width={'5.5rem'}>
                    <HStack spacing={0}>
                      <IconButton
                        aria-label={showPassword ? 'Ocultar' : 'Mostrar'}
                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        size='sm'
                        variant='ghost'
                        onClick={() => setShowPassword((value) => !value)}
                      />
                      <IconButton
                        aria-label='Copiar contraseña'
                        icon={<CopyIcon />}
                        size='sm'
                        variant='ghost'
                        isDisabled={!watchPassword}
                        onClick={onCopyPassword}
                      />
                    </HStack>
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={handleIsInvalidField(errors.confirmPassword?.message)}>
                <Input
                  autoComplete='new-password'
                  type={showPassword ? 'text' : 'password'}
                  placeholder='Confirmar contraseña'
                  {...register('confirmPassword')}
                />
                <FormErrorMessage>{errors.confirmPassword?.message}</FormErrorMessage>
              </FormControl>

              {isEditing && (
                <Text mt={2} fontSize={'sm'} color={'gray.600'}>
                  La contraseña actual no se puede consultar. Si la escribe aquí, se
                  reemplazará por la nueva.
                </Text>
              )}
            </Box>
          </ModalBody>

          <ModalFooter gap={3}>
            <Button variant='ghost' onClick={onClose} isDisabled={isSaving}>
              Cancelar
            </Button>
            <Button
              type='submit'
              colorScheme='blue'
              isLoading={isSaving}
              loadingText={isEditing ? 'Guardando' : 'Creando'}
            >
              {isEditing ? 'Guardar cambios' : 'Crear usuario'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export interface IFormInputs {
  user_name: string;
  email: string;
  rol: string;
  password: string;
  confirmPassword: string;
}

export const schema = (isEditing: boolean) =>
  yup.object({
    user_name: yup.string().required('Requerido'),
    email: yup.string().email('No tiene el formato correcto').required('Requerido'),
    rol: yup.string().required('Requerido'),
    password: isEditing
      ? yup.string().test(
          'min-length-if-present',
          'Debe tener al menos 6 caracteres',
          (value) => !value || value.length >= 6
        )
      : yup.string().min(6, 'Debe tener al menos 6 caracteres').required('Requerido'),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password'), null, ''], 'Las contraseñas no coinciden'),
  });

export default UsersSettings;
